import * as yup from 'yup';
import _get from 'lodash/get';
import {
	RenderConfig,
	TextInput,
	AssetUploadInputOnS3,
	ASSET_TYPE,
	SelectInput,
	NumberInput,
	Row,
	EMPTY_ARRAY,
	Column
} from 'commons-ui';
import { EContentViewCardsType, STAT_TYPES, TAG_TYPES } from './types';

export const customTagDependencyResolver = (props: any, dependencyValues: any) => {
	const allowCustomValues: boolean = _get(dependencyValues, '0', null) !== 'custom';
	const isEditable = props.isEditable[props.mode];
	const { required, disabled } = props;

	if (allowCustomValues && isEditable && !disabled) {
		return { disabled: true };
	}

	if (allowCustomValues && required) {
		return { required: false };
	}

	if (!allowCustomValues && isEditable === disabled) {
		return { disabled: !isEditable };
	}

	if (!allowCustomValues && !required) {
		return { required: true };
	}

	return null;
};

export const FORM_FIELD_KEYS = {
	ZUPEEUSERID: 'zupeeUserId',
	DISPLAY_NAME_EN: 'displayNameEn',
	DISPLAY_NAME_HI: 'displayNameHi',
	DISPLAY_NAME_MR: 'displayNameMr',
	USER_STATS: 'userStats',
	STATS_TYPE: 'statsType',
	STATS_VALUE_EN: 'statsValueEn',
	STATS_VALUE_HI: 'statsValueHi',
	STATS_VALUE_MR: 'statsValueMr',
	LIVESINCE: 'liveSince',
	LIVETILL: 'liveTill',
	CREATED_AT: 'createdAt',
	UPDATED_AT: 'updatedAt',
	VIEW_COUNT: 'viewCount',
	DATA_DURATION_IN_SEC: 'durationInSec',
	THUMBNAIL: 'thumbnailUrl',
	VIDEO: 'videoUrl',
	SHARE_URL: 'shareUrl',
	TAG_INFO_TYPE: 'tagInfoType',
	TAG_INFO_TITLE_EN: 'tagInfoTitleEn',
	TAG_INFO_TITLE_HI: 'tagInfoTitleHi',
	TAG_INFO_TITLE_MR: 'tagInfoTitleMr',
	TAG_INFO_DESC_EN: 'tagInfoDescEn',
	TAG_INFO_DESC_HI: 'tagInfoDescHi',
	TAG_INFO_DESC_MR: 'tagInfoDescMr'
};

export const validationSchemaCreate = yup.object().shape({
	zupeeUserId: yup.number().required('zupee user id is required field'),
	liveSince: yup
		.number()
		.transform((value, originalValue) => {
			if (originalValue === '' || originalValue === null) {
				return undefined;
			}
			return originalValue;
		})
		.test({
			message: 'Start time should be valid date',
			test: (value: any) => value !== '' || value !== null || !Number.isNaN(new Date(value).getTime())
		})
		.test({
			message: 'Start time should be atleast after 2 mins',
			test: (data: any) => {
				if (data === '' || data === null) {
					return false;
				}
				const selectedDate = new Date(data);
				const timeDifference = Math.abs(selectedDate.getTime() - new Date().getTime());
				const isWithin2Minutes = timeDifference <= 2 * 60 * 1000;
				const isValidDate = !Number.isNaN(selectedDate.getTime());
				return isValidDate && !isWithin2Minutes;
			}
		})
		.required('Live Till date is a required field'),
	liveTill: yup
		.number()
		.transform((value, originalValue) => {
			if (originalValue === '' || originalValue === null) {
				return undefined;
			}
			return originalValue;
		})
		.test({
			message: 'Live Till date should be a valid date',
			test: (value: any) => value !== '' || value !== null || !Number.isNaN(new Date(value).getTime())
		})
		.required('Live Till date is a required field'),
	statsType: yup.string().oneOf(Object.keys(EContentViewCardsType), 'stats type not allowed'),
	statsValueEn: yup.string(),
	statsValueHi: yup.string(),
	statsValueMr: yup.string(),
	displayNameEn: yup.string().test('len', 'display name should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return false;
		}
		return val.length >= 2 && val.length <= 16;
	}),
	displayNameHi: yup.string().test('len', 'display name should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return false;
		}
		return val.length >= 2 && val.length <= 16;
	}),
	displayNameMr: yup.string().test('len', 'display name should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return false;
		}
		return val.length >= 2 && val.length <= 16;
	}),
	durationInSec: yup
		.number()
		.test({
			message: 'video duration should be a positive number',
			test: (value: any) => value > 0
		})
		.required('video duration is required field'),
	thumbnailUrl: yup.string().required('thumbnail image is a required field'),
	videoUrl: yup.string().required('spotlight video is a required field'),
	tagInfoType: yup.string().nullable(),
	tagInfoTitleEn: yup.string().test('len', 'tag title should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 16);
	}),
	tagInfoTitleHi: yup.string().test('len', 'tag title should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 16);
	}),
	tagInfoTitleMr: yup.string().test('len', 'tag title should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 16);
	}),
	tagInfoDescEn: yup.string().test('len', 'tag decsription should be atleast 2 characters and not more than 30', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 30);
	}),
	tagInfoDescHi: yup.string().test('len', 'tag decsription should be atleast 2 characters and not more than 30', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 30);
	}),
	tagInfoDescMr: yup.string().test('len', 'tag decsription should be atleast 2 characters and not more than 30', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 30);
	})
});

export const validationSchemaEdit = yup.object().shape({
	zupeeUserId: yup.number().required('zupee user id is required field'),
	liveTill: yup
		.number()
		.transform((value, originalValue) => {
			if (originalValue === '' || originalValue === null) {
				return undefined;
			}
			return originalValue;
		})
		.test({
			message: 'Live Till date should be a valid date',
			test: (value: any) => value !== '' || value !== null || !Number.isNaN(new Date(value).getTime())
		})
		.required('Live Till date is a required field'),
	statsType: yup.string().oneOf(Object.keys(EContentViewCardsType), 'stats type not allowed'),
	statsValueEn: yup.string(),
	statsValueHi: yup.string(),
	statsValueMr: yup.string(),
	displayNameEn: yup.string().test('len', 'display name should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return false;
		}
		return val.length >= 2 && val.length <= 16;
	}),
	displayNameHi: yup.string().test('len', 'display name should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return false;
		}
		return val.length >= 2 && val.length <= 16;
	}),
	displayNameMr: yup.string().test('len', 'display name should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return false;
		}
		return val.length >= 2 && val.length <= 16;
	}),
	durationInSec: yup
		.number()
		.test({
			message: 'video duration should be a positive number',
			test: (value: any) => value > 0
		})
		.required('video duration is required field'),
	thumbnailUrl: yup.string().required('thumbnail image is a required field'),
	videoUrl: yup.string().required('spotlight video is a required field'),
	tagInfoType: yup.string().nullable(),
	tagInfoTitleEn: yup.string().test('len', 'tag title should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 16);
	}),
	tagInfoTitleHi: yup.string().test('len', 'tag title should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 16);
	}),
	tagInfoTitleMr: yup.string().test('len', 'tag title should be atleast 2 characters and not more than 16', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 16);
	}),
	tagInfoDescEn: yup.string().test('len', 'tag decsription should be atleast 2 characters and not more than 30', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 30);
	}),
	tagInfoDescHi: yup.string().test('len', 'tag decsription should be atleast 2 characters and not more than 30', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 30);
	}),
	tagInfoDescMr: yup.string().test('len', 'tag decsription should be atleast 2 characters and not more than 30', (val) => {
		if (val === undefined) {
			return true;
		}
		return val.length === 0 || (val.length >= 2 && val.length <= 30);
	})
});

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.ZUPEEUSERID]: new RenderConfig()
		.setName('zupeeUserId')
		.setLabel('ZupeeUserId Of Spotlight User')
		.setRenderer(NumberInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),
	[FORM_FIELD_KEYS.DISPLAY_NAME_EN]: new RenderConfig()
		.setName('displayNameEn')
		.setLabel('DisplayName English (16)')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.DISPLAY_NAME_HI]: new RenderConfig()
		.setName('displayNameHi')
		.setLabel('DisplayName Hindi (16)')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.DISPLAY_NAME_MR]: new RenderConfig()
		.setName('displayNameMr')
		.setLabel('DisplayName Marathi (16)')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.USER_STATS]: new RenderConfig()
		.setName('userStats')
		.setLabel('User Stats')
		.setRenderProps({
			isArrayField: true,
			isEditable: { create: true, edit: true },
			maxLength: STAT_TYPES.length - 1
		}),
	[FORM_FIELD_KEYS.STATS_TYPE]: new RenderConfig()
		.setName('statsType')
		.setLabel('Stat Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: STAT_TYPES
		}),
	[FORM_FIELD_KEYS.STATS_VALUE_EN]: new RenderConfig()
		.setName('statsValueEn')
		.setLabel('Stat Value English')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.STATS_VALUE_HI]: new RenderConfig()
		.setName('statsValueHi')
		.setLabel('Stat Value Hindi')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.STATS_VALUE_MR]: new RenderConfig()
		.setName('statsValueMr')
		.setLabel('Stat Value Marathi')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.LIVESINCE]: new RenderConfig()
		.setName('liveSince')
		.setLabel('Start Time (liveSince)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			inputType: 'datetime-local',
			defaultValue: new Date().getTime() + 5 * 60000,
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.LIVETILL]: new RenderConfig()
		.setName('liveTill')
		.setLabel('End Time (liveTill)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.CREATED_AT]: new RenderConfig()
		.setName('createdAt')
		.setLabel('Created At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: false
		}),
	[FORM_FIELD_KEYS.UPDATED_AT]: new RenderConfig()
		.setName('updatedAt')
		.setLabel('Updated At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: false
		}),
	[FORM_FIELD_KEYS.VIEW_COUNT]: new RenderConfig()
		.setName('viewCount')
		.setLabel('View Count')
		.setRenderer(NumberInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: false }),
	[FORM_FIELD_KEYS.DATA_DURATION_IN_SEC]: new RenderConfig()
		.setName('durationInSec')
		.setLabel('Video Duration (second)')
		.setRenderer(NumberInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[FORM_FIELD_KEYS.THUMBNAIL]: new RenderConfig()
		.setName('thumbnailUrl')
		.setLabel('Thumbnail Image')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			assetType: ASSET_TYPE.THUMBNAIL,
			usePresignedUrl: true,
			maxFileSizeMB: 0.1
		}),
	[FORM_FIELD_KEYS.VIDEO]: new RenderConfig()
		.setName('videoUrl')
		.setLabel('Spotlight Video')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			assetType: ASSET_TYPE.SPOTLIGHT_VIDEO,
			usePresignedUrl: true,
			maxFileSizeMB: 5
		}),
	[FORM_FIELD_KEYS.SHARE_URL]: new RenderConfig()
		.setName('shareUrl')
		.setLabel('Shorten Share URL (Auto Generated)')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: false, edit: false }, required: false }),
	[FORM_FIELD_KEYS.TAG_INFO_TYPE]: new RenderConfig()
		.setName('tagInfoType')
		.setLabel('Tag Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: TAG_TYPES
		}),
	[FORM_FIELD_KEYS.TAG_INFO_TITLE_EN]: new RenderConfig()
		.setName('tagInfoTitleEn')
		.setLabel('Tag Title English (16)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['tagInfoType'],
			dependencyResolver: customTagDependencyResolver
		}),
	[FORM_FIELD_KEYS.TAG_INFO_TITLE_HI]: new RenderConfig()
		.setName('tagInfoTitleHi')
		.setLabel('Tag Title Hindi (16)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['tagInfoType'],
			dependencyResolver: customTagDependencyResolver
		}),
	[FORM_FIELD_KEYS.TAG_INFO_TITLE_MR]: new RenderConfig()
		.setName('tagInfoTitleMr')
		.setLabel('Tag Title Marathi (16)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['tagInfoType'],
			dependencyResolver: customTagDependencyResolver
		}),
	[FORM_FIELD_KEYS.TAG_INFO_DESC_EN]: new RenderConfig()
		.setName('tagInfoDescEn')
		.setLabel('Tag Description English (30)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['tagInfoType'],
			dependencyResolver: customTagDependencyResolver
		}),
	[FORM_FIELD_KEYS.TAG_INFO_DESC_HI]: new RenderConfig()
		.setName('tagInfoDescHi')
		.setLabel('Tag Description Hindi (30)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['tagInfoType'],
			dependencyResolver: customTagDependencyResolver
		}),
	[FORM_FIELD_KEYS.TAG_INFO_DESC_MR]: new RenderConfig()
		.setName('tagInfoDescMr')
		.setLabel('Tag Description Marathi (30)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			dependencies: ['tagInfoType'],
			dependencyResolver: customTagDependencyResolver
		})
};

export const FORM_LAYOUT_CONFIG = [
	new Row(FORM_FIELD_KEYS.ZUPEEUSERID),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.DISPLAY_NAME_EN)]),
		new Column([new Row(FORM_FIELD_KEYS.DISPLAY_NAME_HI)]),
		new Column([new Row(FORM_FIELD_KEYS.DISPLAY_NAME_MR)])
	]),
	new Row(FORM_FIELD_KEYS.USER_STATS, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.STATS_TYPE)]),
		new Column([new Row(FORM_FIELD_KEYS.STATS_VALUE_EN)]),
		new Column([new Row(FORM_FIELD_KEYS.STATS_VALUE_HI)]),
		new Column([new Row(FORM_FIELD_KEYS.STATS_VALUE_MR)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.LIVESINCE)]), new Column([new Row(FORM_FIELD_KEYS.LIVETILL)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.CREATED_AT)]),
		new Column([new Row(FORM_FIELD_KEYS.UPDATED_AT)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.VIEW_COUNT)]),
		new Column([new Row(FORM_FIELD_KEYS.DATA_DURATION_IN_SEC)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.THUMBNAIL)]), new Column([new Row(FORM_FIELD_KEYS.VIDEO)])]),
	new Row(FORM_FIELD_KEYS.SHARE_URL),
	new Row(FORM_FIELD_KEYS.TAG_INFO_TYPE),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.TAG_INFO_TITLE_EN)]),
		new Column([new Row(FORM_FIELD_KEYS.TAG_INFO_TITLE_HI)]),
		new Column([new Row(FORM_FIELD_KEYS.TAG_INFO_TITLE_MR)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.TAG_INFO_DESC_EN)]),
		new Column([new Row(FORM_FIELD_KEYS.TAG_INFO_DESC_HI)]),
		new Column([new Row(FORM_FIELD_KEYS.TAG_INFO_DESC_MR)])
	])
];
